import { CalendarOutlined } from '@ant-design/icons';
import { L } from '@lib';
import { IDateRange } from '@models';
import { TimeOption, buildTimeOptions, getCalendarStartHour, getCalendarStartMinutes, isTimeTbc, TBC_TIME } from '@utils';
import { DatePicker, Select, Typography } from 'antd';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import React, { useMemo } from 'react';

dayjs.extend(isBetween);

interface IProps {
  dateRange: IDateRange | undefined;
  disabledTimeSlots?: IDateRange[];
  /** Lets an earlier day be picked (a correction: the card is the place that is sure of it). */
  allowPast?: boolean;
  setDateRange: (value: IDateRange) => void;
  disabled?: boolean;
}

const { Text } = Typography;

// The tenant's calendar start time to 20:45 in 15 minute steps. Built per render rather than at
// import: the setting is only known once the app has loaded it.
const buildTimeSelectOptions = (): TimeOption[] => buildTimeOptions(15, getCalendarStartMinutes(), 21 * 60);
// Plus "TBC" as a start: the day is booked, the hour is not (stored as midnight, see
// isTimeTbc). The end then just carries the job's length from midnight, so switching between
// TBC and a real time keeps the duration.
const withTbc = (options: TimeOption[]): TimeOption[] => [{ value: TBC_TIME, label: 'TBC' }, ...options];
// Helper function for formatting with offset
const formatDateWithOffset = (d: dayjs.Dayjs) => d.toISOString();

export const DateTimeRangeSelect: React.FC<IProps> = ({
  dateRange,
  setDateRange,
  disabledTimeSlots,
  allowPast,
  disabled = false,
}) => {
  const timeSelectOptions = useMemo(buildTimeSelectOptions, []);
  const startOptions = useMemo(() => withTbc(timeSelectOptions), [timeSelectOptions]);

  const handleDateChange = (date: dayjs.Dayjs | null) => {
    if (!date) return;
    const startH = dateRange?.startAt ? dayjs(dateRange.startAt).hour() : getCalendarStartHour();
    const startM = dateRange?.startAt ? dayjs(dateRange.startAt).minute() : 0;
    const endH = dateRange?.endAt ? dayjs(dateRange.endAt).hour() : getCalendarStartHour() + 1;
    const endM = dateRange?.endAt ? dayjs(dateRange.endAt).minute() : 0;
    const start = dayjs(date).set('hour', startH).set('minute', startM);
    const end = dayjs(date).set('hour', endH).set('minute', endM);
    setDateRange({
      startAt: formatDateWithOffset(start),
      endAt: formatDateWithOffset(end),
    });
  };

  const handleTimeChange = (value: string, type: 'start' | 'end') => {
    const [h, m] = value.split(':');
    let start = dayjs(dateRange?.startAt);
    let end = dayjs(dateRange?.endAt);

    switch (type) {
      case 'start': {
        // Into or out of TBC: keep the length, move the start.
        const wasTbc = isTimeTbc(start.format());
        const toTbc = value === TBC_TIME;
        const lengthMinutes = end.diff(start, 'minute');
        start = start.hour(Number(h)).minute(Number(m)).second(0);
        if (wasTbc || toTbc) {
          end = start.add(lengthMinutes > 0 ? lengthMinutes : 60, 'minute');
          break;
        }
        const startIndex = timeSelectOptions.findIndex((x) => x.value === value);
        const endIndex = timeSelectOptions.findIndex((x) => x.value === end.format('HH:mm'));
        if (startIndex >= endIndex) {
          const nextEndTime = timeSelectOptions[startIndex + 1]?.value;
          if (nextEndTime) {
            const [hour, minute] = nextEndTime.split(':');
            end = end.hour(Number(hour)).minute(Number(minute)).second(0);
          }
        }
        break;
      }
      case 'end':
        end = end.hour(Number(h)).minute(Number(m)).second(0);
        break;
    }

    setDateRange({
      startAt: formatDateWithOffset(start),
      endAt: formatDateWithOffset(end),
    });
  };

  // === Disable full days ===
  const isDateFullyBlocked = (date: dayjs.Dayjs) => {
    if (!disabledTimeSlots?.length) return false;
    return disabledTimeSlots.some((range) => {
      const start = dayjs(range.startAt).startOf('day');
      const end = dayjs(range.endAt).endOf('day');
      return date.isBetween(start, end, 'day', '[]');
    });
  };

  const disabledDate = (current: any) => {
    const todayBlocked = !allowPast && current < dayjs().startOf('day');
    const inDisabledList = isDateFullyBlocked(current);
    return todayBlocked || inDisabledList;
  };

  // === Disable time slots for selected date ===
  const getDisabledTimesForSelectedDate = () => {
    if (!disabledTimeSlots?.length || !dateRange?.startAt) return [];
    const selectedDate = dayjs(dateRange.startAt).startOf('day');

    const blockedRanges = disabledTimeSlots.filter((range) => {
      const startDay = dayjs(range.startAt).startOf('day');
      const endDay = dayjs(range.endAt).startOf('day');
      return selectedDate.isBetween(startDay, endDay, 'day', '[]');
    });

    const disabledTimes: string[] = [];
    blockedRanges.forEach((range) => {
      const start = dayjs(range.startAt);
      const end = dayjs(range.endAt);
      timeSelectOptions.forEach((option) => {
        const time = dayjs(selectedDate)
          .hour(Number(option.value.split(':')[0]))
          .minute(Number(option.value.split(':')[1]));
        if (time.isBetween(start, end, null, '[)')) {
          disabledTimes.push(option.value);
        }
      });
    });

    return disabledTimes;
  };

  const disabledTimes = getDisabledTimesForSelectedDate();
  const startIsTbc = !!dateRange?.startAt && isTimeTbc(dateRange.startAt);

  // For end times, still respect ordering after start
  const index = useMemo(
    () =>
      [...timeSelectOptions].findIndex(
        (x) => x.value === dayjs(dateRange?.startAt).format('HH:mm')
      ),
    [dateRange?.startAt]
  );

  const endTimes = useMemo(
    () => [...timeSelectOptions].splice(index + 1, timeSelectOptions.length - 1),
    [index]
  );

  if (disabled) {
    const startDate = dateRange?.startAt ? dayjs(dateRange.startAt) : null;
    const endDate = dateRange?.endAt ? dayjs(dateRange.endAt) : null;

    return (
      <>
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <CalendarOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
          <Text strong>
            {startDate && endDate
              ? isTimeTbc(startDate.format())
                ? `${startDate.format('YYYY-MM-DD')}, ${L('TBC')}`
                : `${startDate.format('YYYY-MM-DD')}, ${startDate.format('HH:mm')} - ${endDate.format('HH:mm')}`
              : 'No date selected'}
          </Text>
        </div>
      </>
    );
  }

  return (
    // A grid, not a flex row: the three columns are exactly equal whatever is in them, so the
    // Drop-off row below (same grid) lines up with this one control for control.
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        alignItems: 'center',
        gap: 4,
        width: '100%',
      }}
    >
      <DatePicker
        inputReadOnly
        showTime={false}
        allowClear={false}
        disabledDate={disabledDate}
        format="DD/MM"
        style={{ height: 32 }}
        value={dateRange?.startAt ? dayjs(dateRange.startAt) : null}
        onChange={handleDateChange}
      />
      {/* Both selects sit empty until a date is picked, so they need to say what they are -
          two blank boxes next to a date told the reader nothing. */}
      <Select
        placeholder={L('Start')}
        style={{ height: 32 }}
        options={startOptions.map((opt) => ({
          ...opt,
          disabled: disabledTimes.includes(opt.value),
        }))}
        value={dateRange?.startAt ? dayjs(dateRange.startAt).format('HH:mm') : null}
        onChange={(e) => handleTimeChange(e, 'start')}
      />
      {/* No end to pick while the start is TBC; the length is kept and shows once a time is set. */}
      <Select
        placeholder={L('End')}
        disabled={startIsTbc}
        style={{ height: 32 }}
        options={endTimes.map((opt) => ({
          ...opt,
          disabled: disabledTimes.includes(opt.value),
        }))}
        value={startIsTbc ? '—' : dateRange?.endAt ? dayjs(dateRange.endAt).format('HH:mm') : null}
        onChange={(e) => handleTimeChange(e, 'end')}
      />
    </div>
  );
};
